import { useSelector } from 'react-redux';
import { FEATURE_KEYS } from '@src/configs/featureKeys';
import { useMemo } from 'react';
import { PACKAGE_LEVEL_ORDER } from '@src/constants/package';
import useFeatureFlags from './useFeatureFlags';
import useLocation from './useLocation';

const useMappingPackage = () => {
  const packages = useSelector((state) => state.packages.packages);
  const { user } = useSelector((state) => state.auth);
  const { usingPackage } = useSelector((state) => state.user);
  const { getFeatureValue } = useFeatureFlags();
  const { ip } = useLocation();
  const mappingPackageCodes = getFeatureValue(
    FEATURE_KEYS.MAPPING_PACKAGE_CODES,
    {
      userId: user.id,
      email: user.email,
      phoneNumber: user.phoneNumber,
      ip,
    },
  );

  const enableMappingPackageCodes = useMemo(
    () => mappingPackageCodes && Object.keys(mappingPackageCodes)?.length > 0,
    [mappingPackageCodes],
  );

  const handleGetPackageCodeMapping = ({ level, type, expiresIn } = {}) => {
    const pkg = packages.find(
      (p) => p.level === level && p.type === type && p.expiresIn === expiresIn,
    );
    return pkg?.code;
  };

  const getPurchaseEligibilityUpgrade = (packageCode) => {
    const PURCHASE_ELIGIBILITY_UPGRADE_MAP = {
      'STUDIO-VIP-MONTH-V3': 'STUDIO-VIP-MONTH-V3.1',
      'STUDIO-VIP-YEAR-V3': 'STUDIO-VIP-YEAR-V3.1',
    };
    const mappingPackageCodesOverride = enableMappingPackageCodes
      ? mappingPackageCodes
      : PURCHASE_ELIGIBILITY_UPGRADE_MAP;
    return mappingPackageCodesOverride[packageCode] || packageCode;
  };

  const handleGetPackageCodesByType = ({ type } = {}) => {
    const matchingPackages = packages.filter((p) => p.type === type);
    return matchingPackages.map((pkg) => pkg.code);
  };

  // Resolve the payment-page code for an upgrade target from the current
  // package's own level/type/expiresIn, matched against the live packages list.
  // Version-agnostic (works for V3 and V3.1) because it matches the store, not a
  // static code map, and applies the V3->V3.1 eligibility remap.
  // - targetLevel omitted -> next tier up from `level` (PACKAGE_LEVEL_ORDER).
  const getUpgradePackageCode = ({
    level,
    type,
    expiresIn,
    targetLevel,
  } = {}) => {
    const nextLevel =
      targetLevel ||
      PACKAGE_LEVEL_ORDER[PACKAGE_LEVEL_ORDER.indexOf(level) + 1];
    if (!nextLevel) return undefined;
    const code = packages.find(
      (p) =>
        p.level === nextLevel &&
        p.type === type &&
        p.expiresIn === expiresIn &&
        !p.isTopUp,
    )?.code;
    return code ? getPurchaseEligibilityUpgrade(code) : undefined;
  };

  const handleGetTopUpPackageCode = ({ type, level } = {}) => {
    const topUpPackage = packages.find(
      (p) => p.type === type && p.level === level && p.isTopUp,
    );
    return topUpPackage?.code;
  };

  const getPackageCode = (basePlanId) => {
    if (!basePlanId) return null;
    return basePlanId.split(':')[0];
  };
  const getAutoRenewalPlan = (subscriptionPurchase) => {
    const { nextBasePlanId, basePlanId } = subscriptionPurchase || {};
    const packageCodeMapping = handleGetPackageCodeMapping({
      level: usingPackage?.level,
      type: usingPackage?.type,
      expiresIn: usingPackage?.expiresIn,
    });
    const pkgAutoRenewal = packages.find(
      (pkg) =>
        pkg.code === getPackageCode(nextBasePlanId) ||
        pkg.code === getPackageCode(basePlanId) ||
        pkg.code === packageCodeMapping,
    );
    return pkgAutoRenewal;
  };

  return {
    packages,
    handleGetPackageCodeMapping,
    getPurchaseEligibilityUpgrade,
    getUpgradePackageCode,
    handleGetPackageCodesByType,
    handleGetTopUpPackageCode,
    getAutoRenewalPlan,
  };
};

export { useMappingPackage };
